/** @jsxImportSource @emotion/react */
import { useRouterStore } from 'mobx-state-router';
import React, { useEffect, useCallback, useState } from 'react';
import useSWR from 'swr';
import * as Yup from 'yup';

import {
  Alert,
  Button,
  Card,
  Checkbox,
  FormControl,
  Header,
  InputLabel,
  LoadingSpinner,
} from 'src/common/ui/components';
import { CardContent } from 'src/common/ui/components/Card/Card';
import { states, TEAM_ROLE_OPTIONS as TEAM_ROLES } from 'src/components/forms/schemas/definitions';
import { FileUpload } from 'src/components/pages/providers/Provider.Form.FileUpload';
import {
  SelectInput,
  type SelectOption,
} from 'src/components/pages/providers/Provider.Form.SelectInput';
import { TextInput } from 'src/components/pages/providers/Provider.Form.TextInput';
import { formControlStyles } from 'src/components/pages/providers/Provider.Form.styles';
import { ProviderQuery } from 'src/generated/gql/graphql';
import { LOAD_PROVIDER } from 'src/stores/queries/providers';
import useRootStore from 'src/stores/useRootStore';
import { TIMEZONES } from 'src/util/timezones';

const STATE_OPTIONS: SelectOption[] = states.map(state => ({ label: state, value: state }));

const TEAM_ROLE_OPTIONS: SelectOption[] = Object.keys(TEAM_ROLES).map(key => ({
  label: TEAM_ROLES[key],
  value: key,
}));

const VALIDATOR = Yup.object().shape({
  address: Yup.object()
    .shape({
      street1: Yup.string().nullable(),
      street2: Yup.string().nullable(),
      city: Yup.string().nullable(),
      state: Yup.string()
        .nullable()
        .oneOf([null, ...states]),
      zip: Yup.string().nullable(),
    })
    .nullable(),
  bio: Yup.string().nullable(),
  dob: Yup.date().nullable().required(),
  email: Yup.string().email().nullable().required(),
  firstName: Yup.string().nullable().required(),
  isInactive: Yup.bool().nullable(),
  lastName: Yup.string().nullable().required(),
  middleName: Yup.string().nullable(),
  npi: Yup.string().nullable(),
  patientFacingDisplayName: Yup.string().nullable(),
  phone: Yup.string()
    .nullable()
    .matches(/^\d{10}$/, 'invalid phone number'),
  pronouns: Yup.string().nullable(),
  taxId: Yup.string().nullable(),
  taxonomyCode: Yup.string().nullable(),
  teamRole: Yup.string().nullable().required(),
  timezone: Yup.string().nullable().required(),
});

export const ProviderForm = ({ id }: { id?: string }) => {
  const [formState, setFormState] = useState<{
    address: {
      street1: string | null;
      street2: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
    };
    avatar: {
      id: string | null;
      filename: string | null;
      url: string | null;
    } | null;
    bio: string | null;
    dob: string | null;
    email: string | null;
    firstName: string | null;
    isInactive: boolean;
    lastName: string | null;
    middleName: string | null;
    npi: string | null;
    patientFacingDisplayName: string | null;
    phone: string | null;
    pronouns: string | null;
    taxId: string | null;
    taxonomyCode: string | null;
    teamRole: string | null;
    timezone: string | null;
  }>({
    address: {
      street1: null,
      street2: null,
      city: null,
      state: null,
      zip: null,
    },
    avatar: null,
    bio: null,
    dob: null,
    email: null,
    firstName: null,
    isInactive: false,
    lastName: null,
    middleName: null,
    npi: null,
    patientFacingDisplayName: null,
    phone: null,
    pronouns: null,
    taxId: null,
    taxonomyCode: null,
    teamRole: 'other_staff',
    timezone: null,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const selectedStateOption =
    STATE_OPTIONS.find(option => option.value === formState.address.state) || null;
  const selectedTimezoneOption =
    TIMEZONES.find(option => option.value === formState.timezone) || null;
  const selectedTeamRoleOption =
    TEAM_ROLE_OPTIONS.find(option => option.value === formState.teamRole) || null;

  const { data, error, isLoading } = useSWR<ProviderQuery>(id ? [LOAD_PROVIDER, { id }] : null, {
    revalidateOnFocus: false,
  });
  useEffect(() => {
    if (data?.provider) {
      setFormState({
        address: {
          street1: data.provider.address?.street1 || null,
          street2: data.provider.address?.street2 || null,
          city: data.provider.address?.city || null,
          state: data.provider.address?.state || null,
          zip: data.provider.address?.zip || null,
        },
        avatar: data.provider.avatar?.id
          ? {
              id: data.provider.avatar.id,
              filename: data.provider.avatar.filename || null,
              url: data.provider.avatar.url || null,
            }
          : null,
        dob: data.provider.dob || null,
        email: data.provider.email || null,
        firstName: data.provider.firstName,
        lastName: data.provider.lastName,
        middleName: data.provider.middleName || null,
        phone: data.provider.phone || null,
        bio: data.provider.bio || null,
        isInactive: data.provider.isInactive || false,
        npi: data.provider.npi || null,
        patientFacingDisplayName: data.provider.patientFacingDisplayName || null,
        pronouns: data.provider.pronouns || null,
        taxId: data.provider.taxId || null,
        taxonomyCode: data.provider.taxonomyCode || null,
        teamRole: data.provider.teamRole || null,
        timezone: data.provider.timezone || null,
      });
    }
  }, [data]);

  const validateForm = useCallback(async () => {
    try {
      await VALIDATOR.validate(formState, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError) {
      if (validationError instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        validationError.inner.forEach(err => {
          if (err.path) {
            newErrors[err.path] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  }, [formState]);
  useEffect(() => {
    validateForm();
  }, [id, formState, validateForm]);

  const { providers } = useRootStore();
  const routerStore = useRouterStore();
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const isValid = await validateForm();
      if (isValid) {
        await providers.saveProvider(id ? { id, ...formState } : formState);
        routerStore.goTo('providers');
      }
    },
    [formState, validateForm, id, providers, routerStore],
  );

  if (id && isLoading) {
    return <LoadingSpinner />;
  }
  if (id && error) {
    return <Alert severity="error" message="Error loading provider data" error={error} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardContent>
          <Header variant="subtitle1">Personal & Contact Information</Header>
          <TextInput
            label="First Name"
            name="firstName"
            onChange={e => setFormState({ ...formState, firstName: e.target.value })}
            placeholder="Enter firstname"
            required
            value={formState.firstName}
            error={errors.firstName}
          />
          <TextInput
            label="Middle Name"
            name="middleName"
            onChange={e => setFormState({ ...formState, middleName: e.target.value })}
            placeholder="Enter middlename"
            value={formState.middleName}
            error={errors.middleName}
          />
          <TextInput
            label="Last Name"
            name="lastName"
            onChange={e => setFormState({ ...formState, lastName: e.target.value })}
            placeholder="Enter lastname"
            required
            value={formState.lastName}
            error={errors.lastName}
          />
          <TextInput
            label="Date of Birth"
            name="dob"
            onChange={e => setFormState({ ...formState, dob: e.target.value })}
            placeholder="Enter date of birth"
            required
            type="date"
            value={formState.dob}
            error={errors.dob}
          />
          <TextInput
            label="Email"
            name="email"
            onChange={e => setFormState({ ...formState, email: e.target.value })}
            placeholder="Enter email"
            required
            value={formState.email}
            error={errors.email}
          />
          <TextInput
            label="Phone"
            name="phone"
            type="tel"
            onChange={e => setFormState({ ...formState, phone: e.target.value || null })}
            placeholder="Enter phone number"
            value={formState.phone}
            error={errors.phone}
          />

          <Header variant="subtitle1">Address</Header>
          <TextInput
            label="Street 1"
            name="address.street1"
            onChange={e =>
              setFormState({
                ...formState,
                address: { ...formState.address, street1: e.target.value },
              })
            }
            placeholder="Enter street 1"
            value={formState.address.street1}
            error={errors['address.street1']}
          />
          <TextInput
            label="Street 2"
            name="address.street2"
            onChange={e =>
              setFormState({
                ...formState,
                address: { ...formState.address, street2: e.target.value },
              })
            }
            placeholder="Enter street 2"
            value={formState.address.street2}
            error={errors['address.street2']}
          />
          <TextInput
            label="City"
            name="address.city"
            onChange={e =>
              setFormState({
                ...formState,
                address: { ...formState.address, city: e.target.value },
              })
            }
            placeholder="Enter city"
            value={formState.address.city}
            error={errors['address.city']}
          />
          <SelectInput
            label="State"
            name="state"
            value={selectedStateOption}
            onChange={option =>
              setFormState({
                ...formState,
                address: { ...formState.address, state: option?.value || null },
              })
            }
            options={STATE_OPTIONS}
            error={errors['address.state']}
          />
          <TextInput
            label="Zip Code"
            name="address.zip"
            value={formState.address.zip}
            type="text"
            onChange={e =>
              setFormState({
                ...formState,
                address: { ...formState.address, zip: e.target.value },
              })
            }
            placeholder="Enter zip code"
            error={errors['address.zip']}
          />
          <SelectInput
            label="Timezone"
            name="timezone"
            value={selectedTimezoneOption}
            onChange={option => {
              setFormState({ ...formState, timezone: option?.value || null });
            }}
            options={TIMEZONES}
            required
            error={errors.timezone}
          />

          <Header variant="subtitle1">Role</Header>
          <SelectInput
            label="Role"
            name="teamRole"
            value={selectedTeamRoleOption}
            onChange={option => {
              setFormState({ ...formState, teamRole: option?.value || null });
            }}
            options={TEAM_ROLE_OPTIONS}
            error={errors.teamRole}
            required
          />
          <TextInput
            name="npi"
            label="NPI"
            onChange={e => setFormState({ ...formState, npi: e.target.value })}
            placeholder="Enter NPI"
            value={formState.npi}
            error={errors.npi}
          />
          <TextInput
            name="taxId"
            label="Tax ID"
            onChange={e => setFormState({ ...formState, taxId: e.target.value })}
            placeholder="Enter tax ID"
            value={formState.taxId}
            error={errors.taxId}
          />
          <TextInput
            name="taxonomyCode"
            label="Taxonomy Code"
            onChange={e => setFormState({ ...formState, taxonomyCode: e.target.value })}
            placeholder="Enter taxonomy code"
            value={formState.taxonomyCode}
            error={errors.taxonomyCode}
          />

          <Header variant="subtitle1">Patient-Facing Information</Header>
          <TextInput
            name="patientFacingDisplayName"
            label="Patient-Facing Display Name"
            onChange={e => setFormState({ ...formState, patientFacingDisplayName: e.target.value })}
            placeholder="Enter patient-facing display name"
            value={formState.patientFacingDisplayName}
            error={errors.patientFacingDisplayName}
          />
          <TextInput
            name="pronouns"
            label="Pronouns (e.g. They/Them)"
            onChange={e => setFormState({ ...formState, pronouns: e.target.value })}
            placeholder="Enter pronouns"
            value={formState.pronouns}
            error={errors.pronouns}
          />
          <TextInput
            name="bio"
            label="Bio"
            onChange={e => setFormState({ ...formState, bio: e.target.value })}
            placeholder="Enter bio"
            value={formState.bio}
            error={errors.bio}
          />
          <FileUpload
            label="Avatar"
            name="avatar"
            value={formState.avatar}
            onChange={avatar => setFormState({ ...formState, avatar: avatar?.id ? avatar : null })}
          />
          <FormControl css={formControlStyles}>
            <InputLabel htmlFor="isInactive">Inactive</InputLabel>
            <Checkbox
              id="isInactive"
              name="isInactive"
              label="Inactive"
              checked={formState.isInactive}
              onChange={e => setFormState({ ...formState, isInactive: e.target.checked })}
            />
          </FormControl>
          <Button type="submit" variant="contained" color="primary">
            {id ? 'Update' : 'Create'} Provider
          </Button>
        </CardContent>
      </Card>
    </form>
  );
};
